import {
  collection,
  doc,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  getDoc,
  getDocs,
} from 'firebase/firestore'
import type {
  Unsubscribe,
  FirestoreDataConverter,
  SnapshotOptions,
  WithFieldValue,
  PartialWithFieldValue,
  SetOptions,
  QuerySnapshot,
  QueryDocumentSnapshot,
} from 'firebase/firestore'

import { db } from '../../../firebase'
import type { Gif, GifDocumentData, CreateGifData } from '../types/gif.types'

import { deleteGifFile } from './storageService'
import { processFileComplete } from './mediaService'

const COLLECTION_NAME = 'gifs'

// Firestore Data Converter for Gif documents
export const gifConverter: FirestoreDataConverter<Gif> = {
  toFirestore(
    gif: WithFieldValue<Gif> | PartialWithFieldValue<Gif>,
    _options?: SetOptions
  ): WithFieldValue<GifDocumentData> | PartialWithFieldValue<GifDocumentData> {
    // Convert the Gif object to GifDocumentData format
    // Note: id field is excluded as it's not part of the document data
    const { createdAt, lastModified, ...rest } = gif as Gif

    return {
      ...rest,
      createdAt: createdAt instanceof Date ? createdAt : serverTimestamp(),
      lastModified:
        lastModified instanceof Date ? lastModified : serverTimestamp(),
    }
  },

  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    _options?: SnapshotOptions
  ): Gif {
    const data = snapshot.data(_options) // Handle both legacy and queue-based structures

    // Handle both legacy GIFs (createdAt/lastModified) and queue-based GIFs (uploadedAt/analyzedAt)
    const createdAt = data.createdAt
      ? data.createdAt.toDate()
      : data.uploadedAt
        ? data.uploadedAt.toDate()
        : new Date()

    const lastModified = data.lastModified
      ? data.lastModified.toDate()
      : data.analyzedAt
        ? data.analyzedAt.toDate()
        : createdAt

    return {
      id: snapshot.id,
      createdAt,
      lastModified,
      durationSeconds: data.durationSeconds,
      fileSizeBytes: data.fileSizeBytes || 0,
      gifUrl: data.gifUrl,
      reference: data.reference,
      explicitRating: data.explicitRating,
      popularity: data.popularity || { copies: 0, favorited: 0 },
      // Denormalized retrieval prose (present once approved / post-migration).
      retrievalSearch: data.retrievalSearch,
      publishedAt: data.publishedAt,
      published: data.published,
      // Legacy pre-migration display fields — pass through only if present.
      promptVersion: data.promptVersion,
      visualElements: data.visualElements,
      emotionalProfile: data.emotionalProfile,
      useCases: data.useCases,
    }
  },
}

// Get reference to the gifs collection with converter
export const gifsCollection = collection(db, COLLECTION_NAME).withConverter(
  gifConverter
)

// Get reference to a specific gif document with converter
export const getGifDoc = (id: string) =>
  doc(db, COLLECTION_NAME, id).withConverter(gifConverter)

// Subscribe to all gifs with real-time updates
// Only returns completed GIFs (filters out pending/analyzing items)
export const subscribeToGifs = (
  callback: (gifs: Gif[]) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  // Filter for completed GIFs only (or legacy GIFs without processingStatus field)
  const q = query(
    gifsCollection,
    where('processingStatus', 'in', ['completed', null]),
    orderBy('createdAt', 'desc')
  )

  return onSnapshot(
    q,
    (snapshot: QuerySnapshot<Gif>) => {
      const gifs: Gif[] = snapshot.docs.map((doc) => doc.data())
      callback(gifs)
    },
    (error) => {
      console.error('Error fetching gifs:', error)
      onError?.(error)
    }
  )
}

// Add a new gif to the collection (convert, store, and save metadata)
export const addGif = async (
  gifData: Omit<CreateGifData, 'gifUrl'>,
  file: File,
  conversionOptions: {
    maxWidth?: number
    maxHeight?: number
    frameRate?: number
    quality?: number
  } = {}
): Promise<string> => {
  try {
    // Convert and store the file using the unified service
    const conversionResult = await processFileComplete(file, conversionOptions)

    // Create the complete gif data with the returned URL
    const completeGifData: Omit<Gif, 'id'> = {
      ...gifData,
      gifUrl: conversionResult.gifUrl,
      durationSeconds: conversionResult.durationSeconds,
      fileSizeBytes: conversionResult.fileSizeBytes,
      createdAt: new Date(), // Will be converted to serverTimestamp by converter
      lastModified: new Date(), // Will be converted to serverTimestamp by converter
      // Initialize popularity with defaults
      popularity: {
        copies: 0,
        favorited: 0,
      },
    }

    // Add the document to Firestore (converter handles the transformation)
    const docRef = await addDoc(
      gifsCollection,
      completeGifData as WithFieldValue<Gif>
    )

    return docRef.id
  } catch (error) {
    console.error('Error adding gif:', error)
    throw error
  }
}

// Update an existing gif
export const updateGif = async (
  gifId: string,
  updates: Partial<CreateGifData>,
  newFile?: File,
  conversionOptions: {
    maxWidth?: number
    maxHeight?: number
    frameRate?: number
    quality?: number
  } = {}
): Promise<void> => {
  try {
    const gifRef = getGifDoc(gifId)
    let updateData: Partial<Gif> = { ...updates }

    // If a new file is provided, convert and store it first
    if (newFile) {
      const conversionResult = await processFileComplete(
        newFile,
        conversionOptions
      )

      // Update the data with the new URL and duration
      updateData = {
        ...updateData,
        gifUrl: conversionResult.gifUrl,
        durationSeconds: conversionResult.durationSeconds,
        fileSizeBytes: conversionResult.fileSizeBytes,
      }
    }

    // Add lastModified timestamp
    updateData.lastModified = new Date()

    // Update using the converter-enabled reference
    await updateDoc(gifRef, updateData as PartialWithFieldValue<Gif>)
  } catch (error) {
    console.error('Error updating gif:', error)
    throw error
  }
}

// Delete a gif (both Firestore and Storage)
export const deleteGif = async (
  gifId: string,
  gifUrl: string
): Promise<void> => {
  try {
    // Delete from Firestore first
    const gifRef = getGifDoc(gifId)
    await deleteDoc(gifRef)

    // Then delete from Storage
    await deleteGifFile(gifUrl)
  } catch (error) {
    console.error('Error deleting gif:', error)
    throw error
  }
}

// Get a single gif by ID
export const getGif = async (gifId: string): Promise<Gif | null> => {
  try {
    const gifRef = getGifDoc(gifId)
    const snapshot = await getDoc(gifRef)

    if (snapshot.exists()) {
      return snapshot.data() // Already converted by the converter
    }
    return null
  } catch (error) {
    console.error('Error getting gif:', error)
    throw error
  }
}

// Get all gifs (for one-time fetch)
// Only returns completed GIFs (filters out pending/analyzing items)
export const getGifs = async (): Promise<Gif[]> => {
  try {
    // Filter for completed GIFs only (or legacy GIFs without processingStatus field)
    const q = query(
      gifsCollection,
      where('processingStatus', 'in', ['completed', null]),
      orderBy('createdAt', 'desc')
    )
    const querySnapshot = await getDocs(q)

    return querySnapshot.docs.map((doc) => doc.data()) // Already converted by the converter
  } catch (error) {
    console.error('Error getting gifs:', error)
    throw error
  }
}

// Filter gifs by emotional tags (client-side filtering)
export const getGifsByEmotionalTags = async (
  emotionalTags: string[]
): Promise<Gif[]> => {
  try {
    const allGifs = await getGifs()

    // Filter gifs that contain any of the specified emotional tags
    return allGifs.filter((gif) =>
      emotionalTags.some((tag) =>
        (gif.emotionalProfile?.emotionalTags ?? []).some(
          (gifTag) =>
            gifTag.toLowerCase().includes(tag.toLowerCase()) ||
            tag.toLowerCase().includes(gifTag.toLowerCase())
        )
      )
    )
  } catch (error) {
    console.error('Error filtering gifs by emotional tags:', error)
    throw error
  }
}
